import React from "react";
import { X, Info, Zap, Droplet, Wind, Wheat, Cog, Package, Users, Sun, Moon, CloudFog, FlaskConical, ChevronRight } from "lucide-react";

const SECTIONS = [
  {
    title: "Basics",
    color: "#00E5FF",
    items: [
      { icon: Sun, label: "Daylight Cycle", desc: "Solar panels only generate power during daytime. Night = 0 output. Watch the Sol clock in the top bar." },
      { icon: CloudFog, label: "Dust Storms", desc: "Reduce solar output to 15% (50% with Storm Shielding research). Storms are shown with a red indicator." },
      { icon: Users, label: "Colonists", desc: "Each colonist consumes 0.4 O₂/s, 0.3 H₂O/s, 0.15 food/s. Request more via the sidebar (costs 60 credits)." },
    ],
  },
  {
    title: "Resources",
    color: "#00FA9A",
    items: [
      { icon: Zap,     label: "Power",   desc: "Generated by Solar Panels and Reactors. Consumed by almost every building. Battery stores excess for night." },
      { icon: Droplet, label: "Water",   desc: "Extracted from ice deposits. Consumed by colonists, greenhouses, and oxygen generators." },
      { icon: Wind,    label: "Oxygen",  desc: "Produced by Oxygen Generators from water. Colonists consume it continuously." },
      { icon: Wheat,   label: "Food",    desc: "Grown in Greenhouses. Colonists need food to survive. Stock up early." },
      { icon: Cog,     label: "Metal",   desc: "Mined from ore deposits. Used for building construction." },
      { icon: Package, label: "Parts",   desc: "Manufactured in factories. Required for advanced buildings and research." },
    ],
  },
  {
    title: "Buildings",
    color: "#B042FF",
    items: [
      { icon: Cog, label: "Placement",     desc: "Select a category from the Build bar at the bottom, then click a hex to place. Costs are deducted immediately." },
      { icon: Cog, label: "Power grid",    desc: "All buildings on your colony share one power pool. Ensure generators cover total consumption." },
      { icon: Cog, label: "Condition",     desc: "Buildings slowly degrade over time. Condition < 30% disables them. (Maintenance system coming soon.)" },
      { icon: Cog, label: "Disable/Enable", desc: "Toggle a building off to save power. Disabled buildings don't consume or produce. Power generators are NEVER auto-disabled." },
    ],
  },
  {
    title: "Research",
    color: "#FFD700",
    items: [
      { icon: FlaskConical, label: "One at a time", desc: "Only one technology can be researched at a time. Click Research in the sidebar to open the tech tree." },
      { icon: FlaskConical, label: "Prerequisites", desc: "Each tier requires the previous tier in that branch to be completed first." },
      { icon: FlaskConical, label: "Cost",          desc: "Research costs credits and machine parts. Some techs also require polymers." },
    ],
  },
  {
    title: "Winning",
    color: "#FF8C00",
    items: [
      { icon: Users, label: "Goal", desc: "Survive 5 Sols with all life-support resources (oxygen, water, food) in the positive and at least 8 colonists alive." },
      { icon: ChevronRight, label: "Objectives panel", desc: "Track your progress on the left-side Objectives panel. Green = done, white = in progress." },
    ],
  },
];

export default function InfoModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 55,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.80)", backdropFilter: "blur(6px)",
      fontFamily: "Rajdhani, sans-serif",
    }}>
      <div style={{
        width: "min(860px,94vw)", height: "min(88vh,740px)",
        background: "linear-gradient(150deg,#04070e 60%,#09101e)",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 0 80px #00E5FF18, 0 8px 80px #000",
        display: "flex", flexDirection: "column",
        position: "relative", overflow: "hidden",
      }}>
        {/* starfield */}
        <svg style={{ position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none",opacity:0.3 }}>
          {Array.from({length:60},(_,i)=>(
            <circle key={i} cx={`${(i*137.5)%100}%`} cy={`${(i*97.3)%100}%`} r={i%6===0?1.5:0.7} fill="white" opacity={0.2+(i%4)*0.15}/>
          ))}
        </svg>

        {/* header */}
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"12px 22px", borderBottom:"1px solid rgba(255,255,255,0.07)",
          background:"rgba(0,0,0,0.35)", position:"relative", zIndex:2,
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:38,height:38,display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid #00E5FF",background:"#00E5FF18",boxShadow:"0 0 18px #00E5FF44" }}>
              <Info size={18} color="#00E5FF"/>
            </div>
            <div>
              <div style={{ fontSize:9,letterSpacing:4,color:"#00E5FF",textTransform:"uppercase" }}>Mars Colony</div>
              <div style={{ fontSize:21,fontWeight:700,color:"#fff",letterSpacing:2,textTransform:"uppercase",lineHeight:1.1 }}>Game Guide</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            width:32,height:32,border:"1px solid rgba(255,255,255,0.18)",background:"rgba(255,255,255,0.04)",
            color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
          }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor="#ff3366";e.currentTarget.style.color="#ff3366";}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.18)";e.currentTarget.style.color="#fff";}}
          >
            <X size={16}/>
          </button>
        </div>

        {/* content */}
        <div style={{ flex:1,overflowY:"auto",padding:"24px 22px",position:"relative",zIndex:1 }}>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:20 }}>
            {SECTIONS.map(section => (
              <div key={section.title} style={{
                border:`1px solid ${section.color}30`,
                background:`${section.color}08`,
                padding:"16px",
              }}>
                <div style={{ fontSize:9,letterSpacing:4,color:section.color,textTransform:"uppercase",marginBottom:12 }}>
                  ── {section.title} ──
                </div>
                <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                  {section.items.map(item => {
                    const Icon = item.icon;
                    return (
                      <div key={item.label} style={{ display:"flex",gap:10,alignItems:"flex-start" }}>
                        <div style={{ width:26,height:26,display:"flex",alignItems:"center",justifyContent:"center",border:`1px solid ${section.color}40`,background:`${section.color}10`,flexShrink:0,marginTop:2 }}>
                          <Icon size={13} color={section.color}/>
                        </div>
                        <div>
                          <div style={{ fontSize:13,fontWeight:700,color:"#e0e6f0",letterSpacing:0.5,textTransform:"uppercase" }}>{item.label}</div>
                          <div style={{ fontSize:11,color:"#7a8898",lineHeight:1.5,marginTop:2 }}>{item.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* quick-tip footer */}
          <div style={{ marginTop:20,padding:"12px 16px",border:"1px solid rgba(255,255,255,0.07)",background:"rgba(255,255,255,0.03)" }}>
            <div style={{ fontSize:9,color:"#555",letterSpacing:3,textTransform:"uppercase",marginBottom:6 }}>Quick Tips</div>
            <div style={{ display:"flex",flexWrap:"wrap",gap:"6px 20px" }}>
              {[
                "Build a battery immediately — night will drain power fast",
                "Place solar panels before reactors unlock",
                "Research Hydroponic Yield early for easy food",
                "Keep O₂ & water rates positive at all times",
                "Dust storms last ~2 min real-time — keep battery at 70%+ heading in",
              ].map((tip,i) => (
                <div key={i} style={{ fontSize:11,color:"#7a8898" }}>▸ {tip}</div>
              ))}
            </div>
          </div>
        </div>

        {/* footer */}
        <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)",padding:"8px 22px",background:"rgba(0,0,0,0.3)",position:"relative",zIndex:2 }}>
          <div style={{ fontSize:10,color:"#555",letterSpacing:1 }}>
            Press <span style={{ color:"#00E5FF" }}>ℹ</span> (top right) to toggle this guide · Game saves automatically
          </div>
        </div>
      </div>
    </div>
  );
}
